import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { getIsInvalid, getCssError, ENUM_CALLBACK_TRANFORMS } from '../../helpers/inputsHelpers';
import { City } from '../../interfaces/city-model';
import { Colonia } from '../../interfaces/zip-model';
import { IResponseOportunidad } from '../../interfaces/IResponse';
import { firstValueFrom, debounceTime, Subscription } from 'rxjs';
import { CatalogsService } from '../../../../services/catalogs.service';
import { StepService } from '../../services/step.service';
import { FORM_ADDRESS_BERAU } from '../../helpers/formBuilders';
import { transformInputs, setValidatorRequired } from '../../helpers/inputsTranforms';
import { AddressListener } from './listener';
import { Country } from '../../interfaces/countries';
import { environment } from 'src/environments/environment';
import { State } from '../../interfaces/states.model';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-address-creditrequest',
  templateUrl: './address-creditrequest.component.html',
  styleUrls: ['./address-creditrequest.component.scss'],
})
export class AddressCreditrequestComponent implements OnInit, OnDestroy {
  @Input()
  address: any = {};

  @Input()
  isBetweenActivated = false;

  @Output()
  addressForm = new EventEmitter<any>();

  @Output()
  municipalityNameChanges = new EventEmitter<string>();

  @ViewChild('stateSelect') stateSelect!: ElementRef<HTMLSelectElement>;

  form!: FormGroup;

  paises: Country[] = [];
  states: State[] = [];
  municipalities: City[] = [];
  colonies: Colonia[] = [];

  getInfoHasErrors = false;
  loading = false;

  addressListener!: AddressListener;
  templateSuffix = environment.templateSuffix;

  municipalityName = "";
  isAvalWithErrorSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private catalogService: CatalogsService,
    private dataService: DataService,
    private stepService: StepService
  ) {
    this.initForm();
    this.addressListener = new AddressListener(this.catalogService);

    this.isAvalWithErrorSubscription = this.dataService.getSharedFunction().subscribe((data) => {
      if (data === 'isAvalWithError') {
        this.form.reset();
        this.form.get('cp')!.setValue('');
      }
    });
  }

  initForm() {
    this.form = FORM_ADDRESS_BERAU(this.fb);
    this.form.get('cityId')!.disable();
    this.form.get('municipality')!.disable();
    this.form.get('stateId')!.disable();
  }

  async ngOnInit() {
    await this.initInfo();
    this.initListener();
    this.fillData();
  }

  async initInfo() {
    const states: IResponseOportunidad = await firstValueFrom(
      this.catalogService.getStates()
    );
    this.states = states.data;


    if (this.isBetweenActivated) {
      const countries = await firstValueFrom(
        this.catalogService.getCountries()
      );
      this.paises = countries.countries;
      setValidatorRequired(this.form, 'countryId');
    }
  }

  fillData() {
    this.form.get('calle')!.setValue(this.address?.calle || '');
    this.form.get('numExt')!.setValue(this.address?.numExt || '');
    this.form.get('numInt')!.setValue(this.address?.numInt || '');
    this.form.get('cp')!.setValue(this.address?.cp || '', { emitEvent: false });
    this.changeCP(this.address, true);
    if (this.isBetweenActivated) {
      this.form.get('countryId')!.setValue(this.address?.countryId || '');
    }
  }

  initListener() {
    const control = this.form.get('cp');
    control?.valueChanges
      .pipe(debounceTime(1500))
      .subscribe((value) => {
        let newAddress = { ...this.address, cp: value };
        this.changeCP(newAddress);
      });

    const colonia = this.form.get('colonia');
    colonia?.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
      if (value === '') return;
      const coloniaDesc = this.form.get('coloniaDesc');
      const resp =
        this.colonies.find((ele: Colonia) => value === ele.idAsentaCpCons)
          ?.dasenta! || '';
      coloniaDesc?.setValue(resp);
    });

    const countryId = this.form.get('countryId');
    countryId?.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
      const countryDesc = this.form.get('countryDesc');
      let resp = this.paises.find(
        (pais) => pais.countryId === +value
      )?.countryDesc;
      countryDesc?.setValue(resp);
    });

    this.form.valueChanges.subscribe(() => {
      this.addressForm.emit(this.form);
      this.municipalityNameChanges.emit(this.municipalityName);
    });

    const fields = ['calle', 'numExt', 'numInt', 'colonia'];

    transformInputs(
      this.form,
      fields,
      ENUM_CALLBACK_TRANFORMS.transformInputToUpperCase
    );
  }

  async changeCP(address: any, change: boolean = true) {
    if (change) {
      this.resetFieldsDomicilio();
    }

    if (!address.cp) return;

    this.showLoading();

    try {
      const { municipalities, colonies, cityId, dmnpio, idState, stateNumber, municipalityName } =
        await this.addressListener.requestAddress(address.cp, this.states);

      console.warn({
        municipalities: municipalities,
        colonies: colonies,
        cityId: cityId,
        dmnpio: dmnpio,
        idState: idState
      });
      this.municipalities = municipalities!;
      this.colonies = colonies;
      this.municipalityName = municipalityName

      if (change) {
        this.form.get('stateId')!.setValue(+idState);
        this.form.get('municipality')!.setValue(dmnpio);
        this.form.get('cityId')!.setValue(cityId);
        this.form.get('idAsentaCpConst')!.setValue(address.colonia);
        this.form.get('colonia')!.setValue(address.colonia);
      }

      const coloniaCrtl = this.form.get('colonia');
      if (coloniaCrtl && coloniaCrtl.value) {
        const resp = this.colonies.find((ele: Colonia) => coloniaCrtl.value === ele.idAsentaCpCons)?.dasenta || '';
        this.form.get('coloniaDesc')!.setValue(resp);
      } else {
        const resp = this.colonies.find((ele: Colonia) => colonies[0].idAsentaCpCons === ele.idAsentaCpCons)?.dasenta || '';
        this.form.get('coloniaDesc')!.setValue(resp);
      }

      const stateCrtl = this.form.get('stateId');
      if (stateCrtl && stateCrtl.value) {
        let stateDesc = this.states.find((state) => state.idDaVinci === +stateCrtl.value)?.stateName;
        this.form.get('stateDesc')?.setValue(stateDesc);
      } else {
        let stateDesc = this.states.find((state) => state.idDaVinci === +colonies[0].idState!)?.stateName;
        this.form.get('stateDesc')?.setValue(stateDesc);
      }

      console.log(this.form.value);
    } catch (error) {
      console.error(error);
      this.resetFieldsDomicilio();
      this.getInfoHasErrors = true;
    } finally {
      this.loading = false;
    }
  }

  showLoading() {
    this.getInfoHasErrors = false;
    const activateLoading = ['E2', 'E3', 'E10', 'E20', 'E32', 'E40'];
    this.loading = activateLoading.includes(this.stepService.data.step!);
  }

  resetFieldsDomicilio() {
    this.colonies = [];
    this.municipalities = [];

    const fields = [
      'stateId',
      'cityId',
      'colonia',
      'municipality',
      'coloniaDesc',
      'stateDesc',
      'otherCityDesc',
    ];

    this.addressListener.resetForm(this.form, fields);
  }

  isInvalid(campo: string) {
    return getIsInvalid(this.form, campo);
  }

  hasErrors(campo: string) {
    return getCssError(this.form, campo);
  }

  ngOnDestroy(): void {
    this.isAvalWithErrorSubscription.unsubscribe();
  }
}
